import { Fragment, useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, Link2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ApiError } from "@/api/client";
import { adminApi } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/dates";
import type { ReteResponsabile, ReteScheda, ReteStatoScheda } from "@/types/admin";

// Stato della scheda CRM: icona + testo, mai solo il colore (regola a11y color-not-only).
const STATO_SCHEDA: Record<ReteStatoScheda, { label: string; Icon: LucideIcon; cls: string }> = {
  completa: { label: "Completa", Icon: Check, cls: "text-emerald-700" },
  da_collegare: { label: "Team non collegato", Icon: Link2, cls: "text-amber-700" },
  senza_account: { label: "Manca l'Account", Icon: AlertTriangle, cls: "text-amber-700" },
  senza_team: { label: "Manca il Team", Icon: AlertTriangle, cls: "text-amber-700" },
  // Più Account/Team con lo stesso nome sul CRM: nessuna riparazione automatica possibile,
  // nessuna azione in riga (la scelta di quale sia quello giusto va fatta sul CRM).
  ambigua: { label: "Nome doppio sul CRM", Icon: AlertTriangle, cls: "text-amber-700" },
};

// Stati con «Completa scheda»: solo quelli con un solo lato mancante o da collegare.
// «ambigua» non è tra questi: non si sa quale Account/Team collegare, non si può riparare da qui.
const STATI_RIPARABILI = new Set<ReteStatoScheda>(["da_collegare", "senza_account", "senza_team"]);

// Il backend rifiuta con un codice (Task 8): qui diventa la frase approvata.
const MOTIVO_RIFIUTO: Record<string, string> = {
  incarico_sovrapposto: "Le date si sovrappongono a un incarico precedente.",
  agenzia_incompleta: "La scheda CRM non è completa: prima «Completa scheda».",
  nomi_diversi: "Il nome dell'Account e del Team non coincidono.",
};

function messaggioErrore(e: unknown): string {
  const codice = e instanceof ApiError ? e.detail : "";
  return MOTIVO_RIFIUTO[codice] ?? "Operazione non riuscita. Riprova tra poco.";
}

/** Oggi in locale, "YYYY-MM-DD" (non `toISOString`: a mezzanotte darebbe il giorno UTC). */
function oggiIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Chiave di riga: una scheda può avere solo l'Account o solo il Team. */
function chiave(s: ReteScheda): string {
  return `${s.account_id ?? ""}|${s.team_id ?? ""}`;
}

type Form = { chiave: string; tipo: "assegna" | "togli" };

export function ReteAgenzie() {
  const [schede, setSchede] = useState<ReteScheda[] | null>(null);
  const [responsabili, setResponsabili] = useState<ReteResponsabile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState<string | null>(null);   // chiave della riga in lavorazione
  const [erroreAzione, setErroreAzione] = useState<{ chiave: string; testo: string } | null>(null);

  const carica = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([adminApi.reteAgenzie(), adminApi.reteResponsabili()]);
      setSchede(s);
      setResponsabili(r);
      setError(null);
    } catch {
      setError("Impossibile caricare le agenzie.");
    }
  }, []);
  useEffect(() => { void carica(); }, [carica]);

  async function esegui(k: string, azione: () => Promise<unknown>) {
    setBusy(k);
    setErroreAzione(null);
    try {
      await azione();
      setForm(null);
      await carica();
    } catch (e) {
      setErroreAzione({ chiave: k, testo: messaggioErrore(e) });
    } finally {
      setBusy(null);
    }
  }

  function completaScheda(s: ReteScheda) {
    return esegui(chiave(s), () =>
      adminApi.reteCompletaScheda({ account_id: s.account_id, team_id: s.team_id }));
  }

  function assegna(s: ReteScheda, responsabileId: number, dal: string) {
    if (!s.account_id) return;
    const accountId = s.account_id;
    return esegui(chiave(s), () =>
      adminApi.reteApriIncarico({ responsabile_id: responsabileId, espo_account_id: accountId, dal }));
  }

  function togli(s: ReteScheda, al: string) {
    if (s.incarico_id === null) return;
    const incaricoId = s.incarico_id;
    return esegui(chiave(s), () => adminApi.reteChiudiIncarico(incaricoId, al));
  }

  function apriForm(k: string, tipo: Form["tipo"]) {
    setErroreAzione(null);
    setForm((f) => (f?.chiave === k && f.tipo === tipo ? null : { chiave: k, tipo }));
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (schede === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;

  const incomplete = schede.filter((s) => s.stato !== "completa").length;
  const attivi = responsabili.filter((r) => r.attivo);

  return (
    <div className="space-y-4">
      <AvvisoIncomplete n={incomplete} />

      <section className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agenzia</TableHead>
                <TableHead>Scheda CRM</TableHead>
                <TableHead>Responsabile</TableHead>
                <TableHead className="hidden md:table-cell">Dal</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schede.map((s) => {
                const k = chiave(s);
                const st = STATO_SCHEDA[s.stato];
                const eCompleta = s.stato === "completa";
                const riparabile = STATI_RIPARABILI.has(s.stato);
                const inCorso = busy === k;
                return (
                  <Fragment key={k}>
                    <TableRow>
                      <TableCell className="font-medium text-foreground">{s.nome}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${st.cls}`}>
                          <st.Icon size={14} aria-hidden="true" />
                          {st.label}
                        </span>
                      </TableCell>
                      <TableCell>
                        {s.responsabile_nome
                          ? `${s.responsabile_nome}${s.scoperta ? " (Disattivato)" : ""}`
                          : <span className="text-muted-foreground">Nessuno</span>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell tabular-nums">
                        {s.dal ? formatDate(s.dal) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5 flex-wrap">
                          {riparabile && (
                            <Button size="sm" variant="outline" disabled={inCorso} onClick={() => void completaScheda(s)}>
                              Completa scheda
                            </Button>
                          )}
                          {eCompleta && (
                            <Button size="sm" variant="outline" disabled={inCorso} onClick={() => apriForm(k, "assegna")}>
                              {s.responsabile_id === null ? "Assegna" : "Riassegna"}
                            </Button>
                          )}
                          {eCompleta && s.incarico_id !== null && (
                            <Button size="sm" variant="outline" disabled={inCorso} onClick={() => apriForm(k, "togli")}>
                              Chiudi incarico
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {form?.chiave === k ? (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-muted/20">
                          {form.tipo === "assegna" ? (
                            <FormAssegna
                              scheda={s}
                              responsabili={attivi.filter((r) => r.id !== s.responsabile_id)}
                              busy={inCorso}
                              errore={erroreAzione?.chiave === k ? erroreAzione.testo : null}
                              onConferma={(rid, dal) => void assegna(s, rid, dal)}
                              onAnnulla={() => setForm(null)}
                            />
                          ) : (
                            <FormTogli
                              scheda={s}
                              busy={inCorso}
                              errore={erroreAzione?.chiave === k ? erroreAzione.testo : null}
                              onConferma={(al) => void togli(s, al)}
                              onAnnulla={() => setForm(null)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ) : erroreAzione?.chiave === k ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <p role="alert" className="text-sm text-destructive">{erroreAzione.testo}</p>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

/** Avviso in testa: riguarda righe che in tabella si vedono, ma il conto serve a colpo d'occhio. */
function AvvisoIncomplete({ n }: { n: number }) {
  if (n === 0) return null;
  const testo = n === 1
    ? "1 agenzia con scheda CRM incompleta: non si può assegnare."
    : `${n} agenzie con scheda CRM incompleta: non si possono assegnare.`;
  return (
    <div role="status" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle size={16} aria-hidden="true" className="shrink-0 mt-0.5" />
      <span>{testo}</span>
    </div>
  );
}

const CAMPO_SELECT =
  "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50";

/** Assegna o sposta: responsabile (solo attivi, escluso quello in carica) e data d'inizio. */
function FormAssegna({ scheda, responsabili, busy, errore, onConferma, onAnnulla }: {
  scheda: ReteScheda;
  responsabili: ReteResponsabile[];
  busy: boolean;
  errore: string | null;
  onConferma: (responsabileId: number, dal: string) => void;
  onAnnulla: () => void;
}) {
  const [responsabileId, setResponsabileId] = useState(String(responsabili[0]?.id ?? ""));
  const [dal, setDal] = useState(oggiIso());

  // Riassegnare un'agenzia quando esiste un solo responsabile non è possibile: l'elenco
  // esclude chi la ha già in carico. Meglio dirlo che mostrare un menù vuoto e un
  // «Conferma» spento senza spiegazione.
  if (responsabili.length === 0) {
    return (
      <div className="space-y-3 max-w-2xl">
        <p className="text-sm text-muted-foreground">
          Non c'è un altro responsabile a cui passare l'agenzia. Aggiungilo dalla scheda Responsabili.
        </p>
        <div className="flex justify-end">
          <Button size="sm" variant="outline" type="button" onClick={onAnnulla}>Chiudi</Button>
        </div>
      </div>
    );
  }
  const idR = `rete-assegna-${scheda.account_id}-responsabile`;
  const idD = `rete-assegna-${scheda.account_id}-dal`;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (responsabileId) onConferma(Number(responsabileId), dal);
      }}
      className="space-y-3 max-w-2xl"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={idR} className="text-sm font-medium text-foreground">Responsabile</label>
          <select
            id={idR}
            value={responsabileId}
            required
            onChange={(e) => setResponsabileId(e.target.value)}
            className={CAMPO_SELECT}
          >
            {responsabili.map((r) => (
              <option key={r.id} value={r.id}>{r.nome}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={idD} className="text-sm font-medium text-foreground">Dal</label>
          <Input id={idD} type="date" value={dal} required onChange={(e) => setDal(e.target.value)} />
        </div>
      </div>
      {errore && <p role="alert" className="text-sm text-destructive">{errore}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" type="button" disabled={busy} onClick={onAnnulla}>Annulla</Button>
        <Button size="sm" type="submit" disabled={busy || !responsabileId}>Conferma</Button>
      </div>
    </form>
  );
}

/** Togli: chiude l'incarico aperto alla data scelta, senza riassegnare. */
function FormTogli({ scheda, busy, errore, onConferma, onAnnulla }: {
  scheda: ReteScheda;
  busy: boolean;
  errore: string | null;
  onConferma: (al: string) => void;
  onAnnulla: () => void;
}) {
  const [al, setAl] = useState(oggiIso());
  const idA = `rete-togli-${scheda.incarico_id}-al`;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onConferma(al);
      }}
      className="space-y-3 max-w-2xl"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={idA} className="text-sm font-medium text-foreground">Al</label>
          <Input id={idA} type="date" value={al} required onChange={(e) => setAl(e.target.value)} />
        </div>
      </div>
      {errore && <p role="alert" className="text-sm text-destructive">{errore}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" type="button" disabled={busy} onClick={onAnnulla}>Annulla</Button>
        <Button size="sm" type="submit" disabled={busy}>Conferma</Button>
      </div>
    </form>
  );
}
